import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createBurnCheckedInstruction,
  createTransferCheckedInstruction,
} from '@solana/spl-token';
import type {
  CashoutPaymentInstructions,
  MarketIntentPaymentInstructions,
  SolanaPaymentContext,
  SpinnerPaymentInstructions,
} from '../api/types';

export const TOKEN_PROGRAM_ID = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
export const TOKEN_2022_PROGRAM_ID = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
export const ASSOCIATED_TOKEN_PROGRAM_ID = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';
export const MEMO_PROGRAM_ID = 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr';

export interface DeriveAssociatedTokenAddressInput {
  owner: string;
  mint: string;
  tokenProgramId: string;
}

export function deriveAssociatedTokenAddress(input: DeriveAssociatedTokenAddressInput): string {
  const owner = new PublicKey(input.owner);
  const mint = new PublicKey(input.mint);
  const tokenProgram = new PublicKey(input.tokenProgramId);
  const ataProgram = new PublicKey(ASSOCIATED_TOKEN_PROGRAM_ID);
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ataProgram,
  );
  return ata.toBase58();
}

/** Seller + treasury split-transfer payment (Gold Exchange cash-out and $OBOL marketplace). */
interface SplitTransferPaymentInstructions {
  mint: string;
  sellerAmount: number;
  treasuryAmount: number;
  sellerRecipient: string;
  sellerOwner?: string;
  treasuryRecipient: string;
  reference: string;
  memo: string;
}

function buildSplitTransferPaymentTransaction(input: {
  payerWallet: string;
  payment: SplitTransferPaymentInstructions;
  context: SolanaPaymentContext;
  sellerAtaMismatchMessage: string;
}): Transaction {
  const payer = new PublicKey(input.payerWallet);
  const mint = new PublicKey(input.payment.mint);
  const tokenProgramId = new PublicKey(input.context.tokenProgramId);
  const source = new PublicKey(deriveAssociatedTokenAddress({
    owner: input.payerWallet,
    mint: input.payment.mint,
    tokenProgramId: input.context.tokenProgramId,
  }));
  const sellerRecipient = new PublicKey(input.payment.sellerRecipient);
  const tx = baseTransaction(payer, input.context)
    .add(memoInstruction(input.payment.memo || input.payment.reference, payer));
  if (input.payment.sellerOwner) {
    const expectedSellerRecipient = deriveAssociatedTokenAddress({
      owner: input.payment.sellerOwner,
      mint: input.payment.mint,
      tokenProgramId: input.context.tokenProgramId,
    });
    if (expectedSellerRecipient !== input.payment.sellerRecipient) {
      throw new Error(input.sellerAtaMismatchMessage);
    }
    tx.add(createAssociatedTokenAccountIdempotentInstruction(
      payer,
      sellerRecipient,
      new PublicKey(input.payment.sellerOwner),
      mint,
      tokenProgramId,
      new PublicKey(ASSOCIATED_TOKEN_PROGRAM_ID),
    ));
  }
  tx.add(createTransferCheckedInstruction(
      source,
      mint,
      sellerRecipient,
      payer,
      toTokenAmount(input.payment.sellerAmount),
      input.context.decimals,
      [],
      tokenProgramId,
    ));
  tx.add(createTransferCheckedInstruction(
      source,
      mint,
      new PublicKey(input.payment.treasuryRecipient),
      payer,
      toTokenAmount(input.payment.treasuryAmount),
      input.context.decimals,
      [],
      tokenProgramId,
    ));
  return tx;
}

export function buildCashoutPaymentTransaction(input: {
  payerWallet: string;
  payment: CashoutPaymentInstructions;
  context: SolanaPaymentContext;
}): Transaction {
  return buildSplitTransferPaymentTransaction({
    ...input,
    sellerAtaMismatchMessage: 'Cash-out seller token account does not match the mint token program. Refresh Gold Exchange or recreate the order.',
  });
}

export function buildMarketplacePaymentTransaction(input: {
  payerWallet: string;
  payment: MarketIntentPaymentInstructions;
  context: SolanaPaymentContext;
}): Transaction {
  return buildSplitTransferPaymentTransaction({
    ...input,
    sellerAtaMismatchMessage: 'Marketplace seller token account does not match this mint. Refresh Marketplace or create a new purchase reservation.',
  });
}

export function buildSpinnerPaymentTransaction(input: {
  payerWallet: string;
  payment: SpinnerPaymentInstructions;
  context: SolanaPaymentContext;
}): Transaction {
  const payer = new PublicKey(input.payerWallet);
  const mint = new PublicKey(input.payment.mint);
  const tokenProgramId = new PublicKey(input.context.tokenProgramId);
  const source = new PublicKey(deriveAssociatedTokenAddress({
    owner: input.payerWallet,
    mint: input.payment.mint,
    tokenProgramId: input.context.tokenProgramId,
  }));
  const tx = baseTransaction(payer, input.context)
    .add(memoInstruction(input.payment.memo || input.payment.reference, payer));
  if (input.payment.burnMode === 'true_burn') {
    tx.add(createBurnCheckedInstruction(
      source,
      mint,
      payer,
      toTokenAmount(input.payment.burnAmount),
      input.context.decimals,
      [],
      tokenProgramId,
    ));
  } else {
    tx.add(createTransferCheckedInstruction(
      source,
      mint,
      new PublicKey(input.payment.burnRecipient),
      payer,
      toTokenAmount(input.payment.burnAmount),
      input.context.decimals,
      [],
      tokenProgramId,
    ));
  }
  tx.add(createTransferCheckedInstruction(
    source,
    mint,
    new PublicKey(input.payment.treasuryRecipient),
    payer,
    toTokenAmount(input.payment.treasuryAmount),
    input.context.decimals,
    [],
    tokenProgramId,
  ));
  return tx;
}

function baseTransaction(payer: PublicKey, context: SolanaPaymentContext): Transaction {
  const tx = new Transaction();
  tx.feePayer = payer;
  tx.recentBlockhash = context.latestBlockhash.blockhash;
  return tx;
}

function memoInstruction(memo: string, payer: PublicKey): TransactionInstruction {
  return new TransactionInstruction({
    programId: new PublicKey(MEMO_PROGRAM_ID),
    keys: [{ pubkey: payer, isSigner: true, isWritable: false }],
    data: Buffer.from(memo, 'utf8'),
  });
}

function toTokenAmount(value: number): bigint {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('Token amount must be a safe non-negative integer');
  return BigInt(value);
}
